import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { listTrashed, restoreInvoice } from "../api/invoices";

/* -------------------------------------------------------------------------- */
/*  Formatters                                                                */
/* -------------------------------------------------------------------------- */

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `$${v.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

function ageDays(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

// Retention tone: amber 25-29 days deleted, red ≥30 (TTL should've purged
// but the next Mongo scan can lag ~60s — and legacy ISO-string deleted_at
// docs never expire until the backfill runs).
function retentionTone(days) {
  if (days == null) return null;
  if (days >= 30) return "red";
  if (days >= 25) return "amber";
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                      */
/* -------------------------------------------------------------------------- */

export default function TrashModal({ isOpen, onClose, onRestored, api, triggerRef }) {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState("");
  // Per-row failure state: { [invoice_id]: { kind: "failed", expiresAt } }
  const [rowError, setRowError] = useState({});
  // Per-row inflight state: { [invoice_id]: true }. Object (not Set) so
  // React re-renders the disabled button when this changes.
  const [inflight, setInflight] = useState({});

  const closeBtnRef = useRef(null);

  // Fetch on open / reset on close.
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    setItems(null);
    setError("");
    setRowError({});
    setInflight({});
    listTrashed(api)
      .then((rows) => mounted && setItems(rows))
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e?.message || String(e));
        setItems([]);
      });
    return () => {
      mounted = false;
    };
  }, [isOpen, api]);

  // Body scroll lock + Escape + focus management.
  useEffect(() => {
    if (!isOpen) return;
    // Capture the trigger element now (when the modal opens) rather than
    // reading triggerRef.current in the cleanup — the ref may point elsewhere
    // by the time cleanup runs. (react-hooks/exhaustive-deps.)
    const triggerEl = triggerRef?.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    // Focus the close button when the modal opens. Small timeout so the
    // node exists in the DOM by the time we focus.
    const t = setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      // Return focus to the trigger so keyboard users land back where
      // they started.
      triggerEl?.focus?.();
    };
  }, [isOpen, onClose, triggerRef]);

  // Auto-clear per-row "Failed — try again" labels after their 6s window.
  useEffect(() => {
    const ids = Object.keys(rowError);
    if (ids.length === 0) return;
    const timers = ids
      .map((id) => {
        const expiresAt = rowError[id]?.expiresAt;
        if (!expiresAt) return null;
        const remaining = Math.max(0, expiresAt - Date.now());
        return setTimeout(() => {
          setRowError((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }, remaining);
      })
      .filter(Boolean);
    return () => timers.forEach((t) => clearTimeout(t));
  }, [rowError]);

  if (!isOpen) return null;

  const doRestore = async (inv) => {
    const id = inv.invoice_id;
    if (inflight[id]) return;
    setInflight((p) => ({ ...p, [id]: true }));
    setRowError((p) => {
      if (!p[id]) return p;
      const next = { ...p };
      delete next[id];
      return next;
    });
    // Optimistic remove from the trash list.
    const prevItems = items;
    setItems((cur) => (cur ? cur.filter((it) => it.invoice_id !== id) : cur));
    try {
      const restored = await restoreInvoice(api, id);
      onRestored?.(restored || inv);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Already gone (TTL purged or removed elsewhere). Stay removed —
        // there's nothing to retry. The next time the user opens the
        // modal, the list will be re-fetched fresh.
      } else {
        // Real failure: put the row back and surface a clickable retry.
        setItems(prevItems);
        setRowError((p) => ({
          ...p,
          [id]: { kind: "failed", expiresAt: Date.now() + 6000 },
        }));
      }
    } finally {
      setInflight((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const onBackdropClick = (e) => {
    // Only close when the click is on the backdrop itself, not bubbling
    // up from the panel.
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      data-testid="trash-modal-backdrop"
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.55)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        data-testid="trash-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trash-modal-title"
        style={{
          width: "100%",
          maxWidth: 560,
          maxHeight: "calc(100dvh - 80px)",
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.rule || "#3a362e"}`,
          borderRadius: 14,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px -18px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 18px",
            borderBottom: `1px dashed ${COLORS.rule || "#3a362e"}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id="trash-modal-title"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: COLORS.text,
                marginBottom: 4,
              }}
            >
              // Recently deleted
            </div>
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: COLORS.faint,
                lineHeight: 1.5,
              }}
            >
              Kept for 30 days, then permanently removed.
            </div>
          </div>
          <button
            ref={closeBtnRef}
            data-testid="trash-modal-close"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              border: `1px solid ${COLORS.rule || "#3a362e"}`,
              backgroundColor: "transparent",
              color: "#a8a29a",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              flex: "0 0 auto",
            }}
          >
            <X size={14} strokeWidth={1.8} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {error && (
            <div
              data-testid="trash-modal-error"
              role="alert"
              style={{
                margin: 18,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${COLORS.red}`,
                backgroundColor: "rgba(229,75,60,0.08)",
                color: "#ffb3ab",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}
          {items === null && !error && (
            <div
              data-testid="trash-modal-loading"
              style={{
                padding: "40px 18px",
                textAlign: "center",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12,
                color: COLORS.faint,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              // loading
            </div>
          )}
          {items && items.length === 0 && !error && (
            <div
              data-testid="trash-modal-empty"
              style={{
                padding: "40px 18px",
                textAlign: "center",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12,
                color: COLORS.faint,
                lineHeight: 1.6,
              }}
            >
              No deleted invoices.
            </div>
          )}
          {items && items.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((inv) => (
                <TrashRow
                  key={inv.invoice_id}
                  inv={inv}
                  failed={!!rowError[inv.invoice_id]}
                  inflight={!!inflight[inv.invoice_id]}
                  onRestore={() => doRestore(inv)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Row                                                                        */
/* -------------------------------------------------------------------------- */

function TrashRow({ inv, failed, inflight, onRestore }) {
  const days = ageDays(inv.deleted_at);
  const tone = retentionTone(days);
  const customer = inv.customer?.name || "—";
  const num = inv.invoice_number ? `#${inv.invoice_number}` : "—";
  const ageLabel = inv.deleted_at ? `deleted ${relTime(inv.deleted_at)}` : "";
  const daysLeftSuffix =
    tone === "amber"
      ? ` · ${Math.max(0, 30 - days)} day${30 - days === 1 ? "" : "s"} left`
      : tone === "red"
        ? " · expires anytime"
        : "";
  const ageColor =
    tone === "red" ? COLORS.red : tone === "amber" ? COLORS.warn : "#5c584f";

  return (
    <li
      data-testid={`trash-row-${inv.invoice_id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: "6px 12px",
        padding: "14px 18px",
        borderBottom: `1px solid #2a2824`,
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          letterSpacing: "0.06em",
          color: "#a8a29a",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {num} · {customer}
      </span>
      <span
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 13,
          color: COLORS.text,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          justifySelf: "end",
        }}
      >
        {fmtMoney(inv.total_due)}
      </span>
      <span
        data-testid={`trash-row-${inv.invoice_id}-age`}
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: ageColor,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {ageLabel}
        {daysLeftSuffix}
      </span>
      <span style={{ justifySelf: "end" }}>
        {failed ? (
          <button
            data-testid={`trash-row-${inv.invoice_id}-retry`}
            onClick={onRestore}
            disabled={inflight}
            title="Click to try restoring again"
            style={{
              background: "transparent",
              border: "none",
              color: "#ffb3ab",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.04em",
              cursor: inflight ? "not-allowed" : "pointer",
              padding: "6px 0",
              textDecoration: "underline",
              opacity: inflight ? 0.6 : 1,
            }}
          >
            Failed — try again
          </button>
        ) : (
          <button
            data-testid={`trash-row-${inv.invoice_id}-restore`}
            onClick={onRestore}
            disabled={inflight}
            style={{
              background: "transparent",
              border: `1px solid ${COLORS.yellow}`,
              color: COLORS.yellow,
              padding: "6px 14px",
              borderRadius: 999,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: inflight ? "not-allowed" : "pointer",
              opacity: inflight ? 0.6 : 1,
            }}
          >
            Restore
          </button>
        )}
      </span>
    </li>
  );
}
